import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { contentAPI } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { getLocalizedErrorMessage } from '@/lib/error-i18n';
import i18n from '@/lib/i18n';

export const useContent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const getAllContents = async (status?: 'draft' | 'published', search?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await contentAPI.getAllContents(status, search);

      if (response.success) {
        return { success: true, data: response.data, count: response.count };
      } else {
        throw new Error(response.message || 'Failed to fetch contents');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.fetchContentFailed');
      setError(errorMessage);
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const getContentById = async (id: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await contentAPI.getContentById(id);

      if (response.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to fetch content');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.fetchContentFailed');
      setError(errorMessage);
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const getContentByStrengthId = async (strengthId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await contentAPI.getContentByStrengthId(strengthId);

      if (response.success) {
        return { success: true, data: response.data, count: response.count };
      } else {
        throw new Error(response.message || 'Failed to fetch content');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.fetchContentFailed');
      setError(errorMessage);
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const createContent = async (formData: FormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await contentAPI.createContent(formData);

      if (response.success) {
        toast({
          title: i18n.t("common.success"),
          description: i18n.t("messages.contentCreated"),
        });
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to create content');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.createContentFailed');
      setError(errorMessage);
      
      toast({
        title: i18n.t("common.error"),
        description: errorMessage,
        variant: "destructive",
      });
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const updateContent = async (id: string, formData: FormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await contentAPI.updateContent(id, formData);

      if (response.success) {
        toast({
          title: i18n.t("common.success"),
          description: i18n.t("messages.contentUpdated"),
        });
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to update content');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.updateContentFailed');
      setError(errorMessage);
      
      toast({
        title: i18n.t("common.error"),
        description: errorMessage,
        variant: "destructive",
      });
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const deleteContent = async (id: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await contentAPI.deleteContent(id);

      if (response.success) {
        toast({
          title: i18n.t("common.success"),
          description: i18n.t("messages.contentDeleted"),
        });
        return { success: true };
      } else {
        throw new Error(response.message || 'Failed to delete content');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.deleteContentFailed');
      setError(errorMessage);
      
      toast({
        title: i18n.t("common.error"),
        description: errorMessage,
        variant: "destructive",
      });
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const togglePublish = async (id: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await contentAPI.togglePublish(id);

      if (response.success) {
        toast({
          title: i18n.t("common.success"),
          description: i18n.t("messages.contentStatusUpdated"),
        });
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to toggle publish status');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.updateContentFailed');
      setError(errorMessage);
      
      toast({
        title: i18n.t("common.error"),
        description: errorMessage,
        variant: "destructive",
      });
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const getStats = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await contentAPI.getStats();

      if (response.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to fetch statistics');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.fetchStatisticsFailed');
      setError(errorMessage);
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    getAllContents,
    getContentById,
    getContentByStrengthId,
    createContent,
    updateContent,
    deleteContent,
    togglePublish,
    getStats,
  };
};

// React Query hook for AI content generation
export const useGenerateContent = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { strength: string; domain: string; language: string }) => {
      const response = await contentAPI.generateContent(data);
      return response.data;
    },
    onError: (error: any) => {
      toast({title: getLocalizedErrorMessage(error, "errors.generateContentFailed"),
        variant: "destructive",
      });
    },
  });
};

// React Query hook for AI quiz generation
export const useGenerateQuiz = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { strength: string; short_description: string; language: string }) => {
      const response = await contentAPI.generateQuiz(data);
      return response.data;
    },
    onError: (error: any) => {
      toast({
        title: getLocalizedErrorMessage(error, "errors.generateQuizFailed"),
        variant: "destructive",
      });
    },
  });
};
